import { useEffect, useMemo, useState } from "react"
import { Alert, Box, Card, CardContent, Chip, LinearProgress, Stack, Tooltip, Typography } from "@mui/material"
import SubtaskIcon from "@mui/icons-material/SubdirectoryArrowRightOutlined"
import ProjectIcon from "@mui/icons-material/FolderSpecialOutlined"

import { projectApi, taskApi, timeApi } from "../../api"
import { usePermissions } from "../../permissions"
import { LoadingRows } from "../../components/LoadingState"
import { PRIORITY_COLORS, STATUS_FALLBACK_COLORS } from "../../theme"
import { filledChipSx } from "../../theme/contrast"
import { humanDuration } from "../../time/duration"
import { formatDate } from "../../utils/format"
import { rise } from "../../motion"

/**
 * What somebody is on: the projects they belong to and the tasks with their
 * name on them.
 *
 * ── Why a tab of its own ────────────────────────────────────────────────────
 *
 * "Open tasks: 4" on the overview raises the next question straight away —
 * which four, on what, and is any of it late — and the answer used to be the
 * Tasks page with a person filter set by hand. Here it is the list itself,
 * late work first, each row opening the task where it stands.
 *
 * ── What counts as theirs ───────────────────────────────────────────────────
 *
 * A task they are on as any assignee, not only the first-listed one, and steps
 * included: a step assigned to somebody is their work even when the task above
 * it belongs to someone else. A project counts when they own it or are on its
 * team. Both lists are limited by the server to what the viewer could see.
 *
 * Hours per project appear only where the viewer may read this person's time.
 */

const PAGE = 200

const TASK_VIEWS = [
  { key: "open", label: "Open" },
  { key: "overdue", label: "Overdue" },
  { key: "done", label: "Done" },
  { key: "all", label: "All" },
]

const ROLE_LABEL = { owner: "Owner", member: "Member", lead: "Lead", viewer: "Viewer" }

const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 }

function today() {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function isOverdue(task) {
  if (task.is_terminal || !task.due_date) return false
  return new Date(String(task.due_date).slice(0, 10) + "T00:00:00") < today()
}

export default function PersonWork({ person, mayReadHours, onOpenTask, onOpenProject }) {
  const { can } = usePermissions()
  const mayTasks = can("tasks", "view")
  const mayProjects = can("projects", "view")

  const [projects, setProjects] = useState(null)
  const [tasks, setTasks] = useState(null)
  const [taskTotal, setTaskTotal] = useState(0)
  const [hoursByProject, setHoursByProject] = useState(new Map())
  const [view, setView] = useState("open")
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    setError(null)
    if (mayProjects) {
      projectApi
        .list({ member_id: person.id, limit: 100 })
        .then((data) => live && setProjects(data.projects || []))
        .catch((err) => {
          if (!live) return
          setProjects([])
          setError(err.userMessage || "Their projects could not be loaded.")
        })
    }
    if (mayTasks) {
      taskApi
        .list({ assignee_id: person.id, limit: PAGE })
        .then((data) => {
          if (!live) return
          setTasks(data.tasks || [])
          setTaskTotal(Number(data.total || (data.tasks || []).length))
        })
        .catch((err) => {
          if (!live) return
          setTasks([])
          setError(err.userMessage || "Their tasks could not be loaded.")
        })
    }
    if (mayReadHours) {
      timeApi
        .summary({ group_by: "project", user_id: person.id })
        .then((data) => {
          if (!live) return
          setHoursByProject(new Map((data.groups || []).map((group) => [String(group.group_key), Number(group.tracked_seconds) || 0])))
        })
        .catch(() => live && setHoursByProject(new Map()))
    }
    return () => {
      live = false
    }
  }, [person.id, mayProjects, mayTasks, mayReadHours])

  const counts = useMemo(() => {
    const list = tasks || []
    return {
      open: list.filter((task) => !task.is_terminal).length,
      overdue: list.filter(isOverdue).length,
      done: list.filter((task) => task.is_terminal).length,
      all: list.length,
    }
  }, [tasks])

  /* Late first, then soonest due, then most urgent — the order somebody
     works a list in. Finished work goes newest first. */
  const shown = useMemo(() => {
    const list = (tasks || []).filter((task) =>
      view === "open" ? !task.is_terminal : view === "overdue" ? isOverdue(task) : view === "done" ? task.is_terminal : true,
    )
    return [...list].sort((a, b) => {
      if (a.is_terminal !== b.is_terminal) return a.is_terminal ? 1 : -1
      if (a.is_terminal && b.is_terminal) return new Date(b.completed_at || 0) - new Date(a.completed_at || 0)
      const lateA = isOverdue(a)
      const lateB = isOverdue(b)
      if (lateA !== lateB) return lateA ? -1 : 1
      const dueA = a.due_date ? new Date(a.due_date).getTime() : Infinity
      const dueB = b.due_date ? new Date(b.due_date).getTime() : Infinity
      if (dueA !== dueB) return dueA - dueB
      return (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9)
    })
  }, [tasks, view])

  if (!mayTasks && !mayProjects) {
    return (
      <Alert severity="info">Projects and tasks are not something your role can see.</Alert>
    )
  }

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      {mayProjects ? (
        <Card sx={rise(0)}>
          <CardContent>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="h3" component="h2" sx={{ flex: 1 }}>
                Projects
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {projects ? `${projects.length} they own or are on` : ""}
              </Typography>
            </Stack>
            {projects === null ? (
              <LoadingRows rows={2} height={56} />
            ) : projects.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Not on any project yet.
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
                {projects.map((project, index) => {
                  const total = Number(project.task_count) || 0
                  const done = total - (Number(project.open_task_count) || 0)
                  const seconds = hoursByProject.get(String(project.id)) || 0
                  return (
                    <Box
                      key={project.id}
                      component="button"
                      type="button"
                      onClick={() => onOpenProject?.(project.id)}
                      sx={{
                        appearance: "none",
                        font: "inherit",
                        color: "text.primary",
                        textAlign: "left",
                        cursor: onOpenProject ? "pointer" : "default",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        p: 1.5,
                        transition: "border-color 140ms ease, box-shadow 140ms ease",
                        "&:hover": onOpenProject ? { borderColor: "primary.main", boxShadow: 1 } : undefined,
                        ...rise(index),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <ProjectIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                        <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                          {project.code}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {project.member_role ? (
                          <Chip
                            size="small"
                            label={ROLE_LABEL[project.member_role] || project.member_role}
                            color={project.member_role === "owner" ? "primary" : "default"}
                            variant={project.member_role === "owner" ? "filled" : "outlined"}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" fontWeight={650} noWrap title={project.name}>
                        {project.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {project.client_label || "—"}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={total ? (done / total) * 100 : 0}
                          sx={{ flex: 1, height: 5, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {done} of {total} done
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {Number(project.member_open_tasks) || 0} open task{Number(project.member_open_tasks) === 1 ? "" : "s"} of theirs
                        {mayReadHours && seconds > 0 ? ` · ${humanDuration(seconds)} logged` : ""}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      ) : null}

      {mayTasks ? (
        <Card sx={rise(1)}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Typography variant="h3" component="h2" sx={{ flex: 1, minWidth: 120 }}>
                Tasks
              </Typography>
              {TASK_VIEWS.map((entry) => (
                <Chip
                  key={entry.key}
                  size="small"
                  label={`${entry.label} ${tasks ? counts[entry.key] : ""}`.trim()}
                  color={view === entry.key ? (entry.key === "overdue" ? "error" : "primary") : "default"}
                  variant={view === entry.key ? "filled" : "outlined"}
                  onClick={() => setView(entry.key)}
                />
              ))}
            </Stack>

            {tasks === null ? (
              <LoadingRows rows={4} height={44} />
            ) : shown.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                {view === "overdue"
                  ? "Nothing overdue."
                  : view === "open"
                    ? "Nothing open with their name on it."
                    : view === "done"
                      ? "Nothing finished yet."
                      : "No tasks assigned."}
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {shown.map((task, index) => {
                  const late = isOverdue(task)
                  return (
                    <Box
                      key={task.id}
                      component="button"
                      type="button"
                      onClick={() => onOpenTask?.(task.id)}
                      aria-label={`Open ${task.task_code}`}
                      sx={{
                        appearance: "none",
                        border: 0,
                        background: "none",
                        font: "inherit",
                        color: "text.primary",
                        textAlign: "left",
                        width: "100%",
                        cursor: onOpenTask ? "pointer" : "default",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        px: 1,
                        py: 0.9,
                        borderRadius: 1.5,
                        "&:hover": { bgcolor: "action.hover" },
                        ...rise(Math.min(index, 10)),
                      }}
                    >
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: PRIORITY_COLORS[task.priority], flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", flexShrink: 0, minWidth: 52 }}>
                        {task.task_code}
                      </Typography>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                          {task.parent_task_id ? (
                            <Tooltip title={task.parent_title ? `A step inside “${task.parent_title}”` : "A step inside another task"}>
                              <SubtaskIcon sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0 }} />
                            </Tooltip>
                          ) : null}
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: 550,
                              textDecoration: task.is_terminal ? "line-through" : "none",
                              color: task.is_terminal ? "text.secondary" : "text.primary",
                            }}
                          >
                            {task.title}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {[
                            task.parent_task_id && task.parent_code ? `in ${task.parent_code}` : null,
                            task.project_name,
                            task.client_label && task.client_id !== "0" ? task.client_label : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "No project"}
                        </Typography>
                      </Box>
                      {task.subtask_total > 0 ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${task.subtask_done}/${task.subtask_total}`}
                          sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                        />
                      ) : null}
                      <Typography
                        variant="caption"
                        sx={{
                          flexShrink: 0,
                          minWidth: 84,
                          textAlign: "right",
                          color: late ? "error.main" : "text.secondary",
                          fontWeight: late ? 700 : 400,
                        }}
                      >
                        {task.is_terminal
                          ? task.completed_at
                            ? `Done ${formatDate(task.completed_at)}`
                            : "Done"
                          : task.due_date
                            ? `${late ? "Overdue · " : "Due "}${formatDate(task.due_date)}`
                            : "No due date"}
                      </Typography>
                      <Chip
                        size="small"
                        label={task.status_label}
                        sx={{
                          height: 20,
                          fontSize: 10,
                          flexShrink: 0,
                          ...filledChipSx(task.status_color || STATUS_FALLBACK_COLORS[task.status_name]),
                        }}
                      />
                    </Box>
                  )
                })}
              </Stack>
            )}
            {tasks && taskTotal > tasks.length ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                Showing the first {tasks.length} of {taskTotal}. The Tasks page, filtered to them, has the rest.
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  )
}
